import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext.jsx';
import AdminLayout from '../components/AdminLayout.jsx';
import AdminNavigation from '../components/admin/AdminNavigation.jsx';
import UserDetailsModal from '../components/admin/UserDetailsModal.jsx';
import PlanModal from '../components/admin/PlanModal.jsx';
import QuickActionTemplateManager from '../components/admin/QuickActionTemplateManager.jsx';
import GlobalElevenLabsSettings from '../components/admin/GlobalElevenLabsSettings.jsx';
import AdminNotifications from './admin/AdminNotifications.jsx';
import QueueMonitoringDashboard from '../components/QueueMonitoringDashboard.jsx';

const AdminDashboard = () => {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin access
  useEffect(() => {
    const checkAdminAccess = async () => {
      // Wait for Clerk to be fully loaded
      if (!isLoaded) {
        return;
      }

      // If not signed in after Clerk is loaded, redirect to sign-in
      if (!isSignedIn) {
        navigate('/sign-in?redirect_url=' + encodeURIComponent('/admin'));
        return;
      }

      try {
        const token = await getToken();
        
        if (!token) {
          navigate('/sign-in?redirect_url=' + encodeURIComponent('/admin'));
          return;
        }
        
        const response = await fetch('https://parrotmate.com/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          
          if (userData.is_admin) {
            setIsAdmin(true);
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/sign-in?redirect_url=' + encodeURIComponent('/admin'));
        }
      } catch (error) {
        console.error('Admin access check failed:', error);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    checkAdminAccess();
  }, [isLoaded, isSignedIn, getToken, navigate]);

  if (!isLoaded || loading) {
    return (
      <div className={"min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center"} data-theme={theme}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className={"text-gray-600 dark:text-gray-300 dark:text-gray-600"}>
            {!isLoaded ? 'Loading authentication...' : 'Checking admin access...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className={"min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center"} data-theme={theme}>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Admin Access Required</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6 dark:text-gray-600">Please sign in to access the admin dashboard.</p>
          <button
            onClick={() => navigate('/sign-in?redirect=/admin')}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:text-white"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className={"min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center"} data-theme={theme}>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6 dark:text-gray-600">You don't have admin privileges.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:text-white"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'users', name: 'Users', icon: '👥' },
    { id: 'notifications', name: 'Notifications', icon: '🔔' },
    { id: 'plans', name: 'Plans', icon: '💳' },
    { id: 'promo-codes', name: 'Promo Codes', icon: '🎫' },
    { id: 'phone-numbers', name: 'Phone Numbers', icon: '📞' },
    { id: 'parrots', name: 'Parrots', icon: '🦜' },
    { id: 'quick-action-templates', name: 'Quick Action Templates', icon: '📝' },
    { id: 'providers', name: 'Providers', icon: '⚙️' },
    { id: 'analytics', name: 'Analytics', icon: '📊' },
    { id: 'queue-monitoring', name: 'Queue Monitoring', icon: '📈' },
    { id: 'system-health', name: 'System Health', icon: '🔧' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'users':
        return <UsersTab />;
      case 'notifications':
        return <AdminNotifications />;
      case 'plans':
        return <PlansTab />;
      case 'promo-codes':
        return <PromoCodesTab />;
      case 'phone-numbers':
        return <PhoneNumbersTab />;
      case 'parrots':
        return <ParrotsTab />;
      case 'quick-action-templates':
        return <QuickActionTemplateManager />;
      case 'providers':
        return <GlobalElevenLabsSettings />;
      case 'analytics':
        return <AnalyticsTab />;
      case 'queue-monitoring':
        return <QueueMonitoringDashboard />;
      case 'system-health':
        return <SystemHealthTab />;
      default:
        return <UsersTab />;
    }
  };

  return (
    <AdminLayout>
      {/* Page Title */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h2>
        <p className="mt-1 text-gray-600 dark:text-gray-300 dark:text-gray-600">Manage ParrotMate Operations</p>
      </div>

      {/* Navigation Tabs */}
      <AdminNavigation 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        tabs={tabs}
        aria-label="Admin dashboard navigation"
      />

      {/* Tab Content Container */}
      <div 
        className={"bg-white dark:bg-gray-800 border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-lg shadow transition-colors"}
        role="tabpanel"
        id={`${activeTab}-panel`}
        aria-labelledby={`admin-tab-${activeTab}`}
        tabIndex={0}
        data-theme={theme}
      >
        <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
          {renderTabContent()}
        </div>
      </div>
    </AdminLayout>
  );
};

// Users Tab Component
const UsersTab = () => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [search]);

  const fetchUsers = async () => {
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const response = await fetch(`https://parrotmate.com/api/admin/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = async (user) => {
    try {
      const token = await getToken();
      const response = await fetch(`https://parrotmate.com/api/admin/users/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setSelectedUser(userData);
        setShowUserModal(true);
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`https://parrotmate.com/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchUsers();
        setShowUserModal(false);
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Users Management</h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage user accounts and permissions
          </p>
        </div>
        <div className="flex space-x-4">
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`px-4 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
          />
          <button
            onClick={() => {
              setSelectedUser(null);
              setShowUserModal(true);
            }}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:text-white"
          >
            Create New User
          </button>
        </div>
      </div>

      <div className={`${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      } shadow rounded-lg border overflow-hidden`}>
        <ul className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {users.map((user) => (
            <li key={user.id}>
              <div className={`px-4 py-4 flex items-center justify-between transition-colors ${
                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
              }`}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-teal-600 flex items-center justify-center">
                      <span className="text-white font-medium dark:text-white">
                        {user.first_name?.[0] || user.email[0].toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {user.first_name} {user.last_name}
                      </p>
                      {user.is_admin && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-600 text-white dark:text-white">
                          Admin
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{user.email}</p>
                    <div className="flex items-center mt-1">
                      {user.subscription_status && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.subscription_status === 'active' ? 'bg-green-600 text-white' :
                          user.subscription_status === 'trialing' ? 'bg-blue-600 text-white' :
                          'bg-gray-600 text-white'
                        }`}>
                          {user.subscription_status}
                        </span>
                      )}
                      {user.plan_name && (
                        <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          {user.plan_name} - ${user.plan_price / 100}/month
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleUserClick(user)}
                    className="px-3 py-1 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 transition-colors dark:text-white"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => deleteUser(user.id)}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors dark:text-white"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* User Details Modal */}
      {showUserModal && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => setShowUserModal(false)}
          onRefresh={fetchUsers}
        />
      )}
    </div>
  );
};

// Plans Tab Component
const PlansTab = () => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/admin/plans', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Plans Management</h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Create and manage subscription plans
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedPlan(null);
            setShowPlanModal(true);
          }}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:text-white"
        >
          Create New Plan
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div key={plan.version_id} className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6 border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Version {plan.version}</p>
              </div>
              <div className="flex flex-col space-y-1">
                {plan.is_current && (
                  <span className="px-2 py-1 bg-green-600 text-white text-xs rounded dark:text-white">
                    Current
                  </span>
                )}
                {plan.is_popular && (
                  <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded dark:text-white">
                    Popular
                  </span>
                )}
                {plan.badge && (
                  <span className="px-2 py-1 bg-purple-600 text-white text-xs rounded dark:text-white">
                    {plan.badge}
                  </span>
                )}
              </div>
            </div>
            
            <div className="space-y-2 mb-4">
              <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                ${(plan.monthly_fee / 100).toFixed(2)}/month
              </p>
              {plan.first_month_fee && (
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                  First month: ${(plan.first_month_fee / 100).toFixed(2)}
                </p>
              )}
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                {plan.minutes_included} minutes included
              </p>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                {plan.phone_numbers_included} phone number(s)
              </p>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                {plan.retention_days} days retention
              </p>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                Overage: ${(plan.voice_overage_rate / 100).toFixed(2)}/min
              </p>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                Extra phone: ${(plan.extra_phone_rate / 100).toFixed(2)}/month
              </p>
              {plan.is_public !== undefined && (
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                  Visibility: {plan.is_public ? 'Public' : 'Private'}
                </p>
              )}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setSelectedPlan(plan);
                  setShowPlanModal(true);
                }}
                className="flex-1 px-3 py-2 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 transition-colors dark:text-white"
              >
                Edit
              </button>
              <button
                className={`flex-1 px-3 py-2 text-sm rounded transition-colors ${
                  plan.is_active
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {plan.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Plan Modal */}
      {showPlanModal && (
        <PlanModal
          plan={selectedPlan}
          onClose={() => setShowPlanModal(false)}
          onRefresh={fetchPlans}
        />
      )}
    </div>
  );
};

// Promo Codes Tab Component
const PromoCodesTab = () => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/admin/promo/list', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPromoCodes(data);
      }
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const deletePromoCode = async (promoId) => {
    if (!confirm('Are you sure you want to delete this promo code?')) {
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`https://parrotmate.com/api/admin/promo/${promoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchPromoCodes();
      }
    } catch (error) {
      console.error('Failed to delete promo code:', error);
    }
  };

  const togglePromoStatus = async (promoId, currentStatus) => {
    try {
      const token = await getToken();
      const response = await fetch(`https://parrotmate.com/api/admin/promo/${promoId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_active: !currentStatus
        })
      });

      if (response.ok) {
        fetchPromoCodes();
      }
    } catch (error) {
      console.error('Failed to toggle promo status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Loading promo codes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Promo Codes Management</h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Create and manage promotional discount codes
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedPromo(null);
            setShowPromoModal(true);
          }}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:text-white"
        >
          Create New Promo Code
        </button>
      </div>

      <div className={`${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      } shadow rounded-lg border overflow-hidden`}>
        <ul className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {promoCodes.map((promo) => (
            <li key={promo.id}>
              <div className={`px-4 py-4 flex items-center justify-between transition-colors ${
                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
              }`}>
                <div className="flex-1">
                  <div className="flex items-center">
                    <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{promo.code}</h3>
                    <div className="ml-4 flex space-x-2">
                      {promo.is_active ? (
                        <span className="px-2 py-1 bg-green-600 text-white text-xs rounded dark:text-white">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-red-600 text-white text-xs rounded dark:text-white">
                          Inactive
                        </span>
                      )}
                      <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded dark:text-white">
                        {promo.type === 'percentage' ? `${promo.discount_value}%` : `$${promo.discount_value}`}
                      </span>
                    </div>
                  </div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mt-1`}>{promo.description}</p>
                  <div className={`flex items-center mt-2 space-x-4 text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    <span>Usage: {promo.usage_count}/{promo.max_uses || '∞'}</span>
                    {promo.valid_from && (
                      <span>From: {new Date(promo.valid_from).toLocaleDateString()}</span>
                    )}
                    {promo.valid_until && (
                      <span>Until: {new Date(promo.valid_until).toLocaleDateString()}</span>
                    )}
                    <span>Created: {new Date(promo.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedPromo(promo);
                      setShowPromoModal(true);
                    }}
                    className="px-3 py-1 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 dark:text-white"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => togglePromoStatus(promo.id, promo.is_active)}
                    className={`px-3 py-1 text-sm rounded ${
                      promo.is_active
                        ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {promo.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => deletePromoCode(promo.id)}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 dark:text-white"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Promo Code Modal */}
      {showPromoModal && (
        <PromoCodeModal
          promo={selectedPromo}
          onClose={() => setShowPromoModal(false)}
          onRefresh={fetchPromoCodes}
        />
      )}
    </div>
  );
};

// Phone Numbers Tab Component
const PhoneNumbersTab = () => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [searchParams, setSearchParams] = useState({
    areaCode: '',
    contains: '',
    countryCode: 'US'
  });

  useEffect(() => {
    fetchPhoneNumbers();
  }, []);

  const fetchPhoneNumbers = async () => {
    try {
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/admin/phone/numbers', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPhoneNumbers(data);
      }
    } catch (error) {
      console.error('Failed to fetch phone numbers:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchAvailableNumbers = async () => {
    setSearchingNumbers(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams(searchParams);
      const response = await fetch(`https://parrotmate.com/api/admin/phone/available?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableNumbers(data);
        setShowAssignModal(true);
      }
    } catch (error) {
      console.error('Failed to search available numbers:', error);
    } finally {
      setSearchingNumbers(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Loading phone numbers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Phone Numbers Management</h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Search and manage phone number assignments
          </p>
        </div>
        <div className="flex space-x-4">
          <input
            type="text"
            placeholder="Area Code"
            value={searchParams.areaCode}
            onChange={(e) => setSearchParams({...searchParams, areaCode: e.target.value})}
            className={`px-3 py-2 rounded-lg w-24 border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
          />
          <input
            type="text"
            placeholder="Contains"
            value={searchParams.contains}
            onChange={(e) => setSearchParams({...searchParams, contains: e.target.value})}
            className={`px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
          />
          <button
            onClick={searchAvailableNumbers}
            disabled={searchingNumbers}
            className={`px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors focus:ring-2 focus:ring-teal-500 ${
              theme === 'dark' ? 'focus:ring-offset-gray-800' : 'focus:ring-offset-2'
            }`}
          >
            {searchingNumbers ? 'Searching...' : 'Find Numbers'}
          </button>
        </div>
      </div>

      <div className={`${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      } shadow rounded-lg border overflow-hidden transition-colors`}>
        <ul className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {phoneNumbers.map((number) => (
            <li key={number.id}>
              <div className={`px-4 py-4 flex items-center justify-between transition-colors ${
                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
              }`}>
                <div>
                  <p className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{number.number}</p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{number.friendly_name}</p>
                  {number.user_email && (
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      Assigned to: {number.first_name} {number.last_name} ({number.user_email})
                    </p>
                  )}
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                    Purchased: {new Date(number.purchased_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex space-x-2">
                  {number.user_id ? (
                    <span className={`px-3 py-1 text-sm rounded transition-colors ${
                      theme === 'dark' 
                        ? 'bg-green-600 text-white' 
                        : 'bg-green-100 text-green-800 border border-green-200'
                    }`}>
                      Assigned
                    </span>
                  ) : (
                    <span className={`px-3 py-1 text-sm rounded transition-colors ${
                      theme === 'dark' 
                        ? 'bg-yellow-600 text-white' 
                        : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                    }`}>
                      Unassigned
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Assign Number Modal */}
      {showAssignModal && (
        <AssignNumberModal
          availableNumbers={availableNumbers}
          onClose={() => setShowAssignModal(false)}
          onRefresh={fetchPhoneNumbers}
        />
      )}
    </div>
  );
};

// Parrots Tab Component
const ParrotsTab = () => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [parrots, setParrots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedParrot, setSelectedParrot] = useState(null);
  const [showParrotModal, setShowParrotModal] = useState(false);

  useEffect(() => {
    fetchParrots();
  }, []);

  const fetchParrots = async () => {
    try {
      setError(null);
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/admin/parrots', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setParrots(data);
      } else {
        setError('Failed to load parrots. Please try again.');
      }
    } catch (error) {
      console.error('Failed to fetch parrots:', error);
      setError('Failed to load parrots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Loading parrots...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Parrots Management</h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            View and manage AI voice parrots across all users
          </p>
        </div>
        
        <div className={`${
          theme === 'dark' 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        } shadow rounded-lg border p-8`}>
          <div className="text-center">
            <div className={`text-6xl mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`}>⚠️</div>
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
              Error Loading Parrots
            </h3>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              {error}
            </p>
            <button
              onClick={fetchParrots}
              className={`px-4 py-2 text-white rounded-lg transition-colors ${
                theme === 'dark'
                  ? 'bg-teal-600 hover:bg-teal-700'
                  : 'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Parrots Management</h2>
        <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          View and manage AI voice parrots across all users
        </p>
      </div>

      <div className={`${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      } shadow rounded-lg border overflow-hidden`}>
        {parrots.length === 0 ? (
          <div className="p-8 text-center">
            <div className={`text-6xl mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`}>🦜</div>
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
              No Parrots Found
            </h3>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No AI voice parrots have been created yet.
            </p>
          </div>
        ) : (
          <ul className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {parrots.map((parrot) => (
              <li key={parrot.id}>
                <div className={`px-4 py-4 flex items-center justify-between transition-colors ${
                  theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{parrot.name}</h3>
                      {parrot.assigned_phone_number && (
                        <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          📞 {parrot.assigned_phone_number}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                      Owner: {parrot.first_name} {parrot.last_name} ({parrot.user_email})
                    </p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mt-1 truncate max-w-md`}>
                      {parrot.first_message}
                    </p>
                    <div className="flex items-center mt-2 space-x-4">
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        Voice: {parrot.voice_id}
                      </span>
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        Model: {parrot.model_id}
                      </span>
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        Temp: {parrot.temperature}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedParrot(parrot);
                      setShowParrotModal(true);
                    }}
                    className={`px-3 py-1 text-white text-sm rounded transition-colors ${
                      theme === 'dark'
                        ? 'bg-teal-600 hover:bg-teal-700'
                        : 'bg-teal-600 hover:bg-teal-700'
                    }`}
                  >
                    Edit
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Parrot Modal */}
      {showParrotModal && selectedParrot && (
        <ParrotModal
          parrot={selectedParrot}
          onClose={() => setShowParrotModal(false)}
          onRefresh={fetchParrots}
        />
      )}
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab = () => {
  const { theme } = useTheme();
  const { getToken } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7_days');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await fetch(`https://parrotmate.com/api/admin/analytics/live?timeRange=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        throw new Error('Failed to fetch analytics data');
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, icon, trend, trendValue, isLoading }) => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {title}
          </p>
          <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {isLoading ? (
              <div className={`animate-pulse h-8 w-16 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
            ) : (
              value
            )}
          </div>
          {trend && trendValue && !isLoading && (
            <p className={`text-sm ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500'} flex items-center mt-1`}>
              <span className="mr-1">
                {trend === 'up' ? '↗' : trend === 'down' ? '↘' : '→'}
              </span>
              {trendValue}
            </p>
          )}
        </div>
        <div className={`text-3xl ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  const ChartPlaceholder = ({ title, height = 'h-64' }) => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        {title}
      </h3>
      <div className={`${height} flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
      } rounded-lg`}>
        <div className="text-center">
          <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>📈</div>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Chart visualization coming soon
          </p>
        </div>
      </div>
    </div>
  );

  const UserGrowthChart = () => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        User Growth
      </h3>
      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
        </div>
      ) : analytics?.trends?.userGrowth?.length > 0 ? (
        <div className="h-64 overflow-x-auto">
          <div className="flex items-end justify-between h-full space-x-2 min-w-full">
            {analytics.trends.userGrowth.slice(-14).map((data, index) => {
              const maxUsers = Math.max(...analytics.trends.userGrowth.map(d => d.totalUsers));
              const height = maxUsers > 0 ? (data.totalUsers / maxUsers) * 200 : 0;
              return (
                <div key={index} className="flex flex-col items-center flex-1 min-w-0">
                  <div className="flex flex-col items-center justify-end h-48 w-full">
                    <div 
                      className="bg-teal-500 rounded-t w-full min-h-1 transition-all duration-300"
                      style={{ height: `${height}px` }}
                      title={`${data.totalUsers} users on ${new Date(data.date).toLocaleDateString()}`}
                    ></div>
                  </div>
                  <div className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} truncate w-full text-center`}>
                    {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} font-medium`}>
                    {data.totalUsers}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center">
          <div className="text-center">
            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>📈</div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No user growth data available
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const TopPerformingParrots = () => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        Top Performing Parrots
      </h3>
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-3">
              <div className={`animate-pulse h-4 w-4 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
              <div className={`animate-pulse h-4 flex-1 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
              <div className={`animate-pulse h-4 w-16 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
            </div>
          ))}
        </div>
      ) : analytics?.topPerformingParrots?.length > 0 ? (
        <div className="space-y-3">
          {analytics.topPerformingParrots.slice(0, 10).map((parrot, index) => (
            <div key={parrot.parrotId} className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  index === 0 ? 'bg-yellow-500 text-white' :
                  index === 1 ? 'bg-gray-400 text-white' :
                  index === 2 ? 'bg-orange-600 text-white' :
                  theme === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {parrot.parrotName}
                  </p>
                  <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {parrot.userName}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4 flex-shrink-0">
                <div className="text-right">
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {parrot.calls} calls
                  </p>
                  <p className={`text-xs ${parrot.successRate >= 90 ? 'text-green-600' : parrot.successRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {parrot.successRate}% success
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    ${parrot.revenue.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center">
          <div className="text-center">
            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>🦜</div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No parrot performance data available
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const CallVolumeChart = () => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        Call Volume Over Time
      </h3>
      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
        </div>
      ) : analytics?.trends?.callVolume?.length > 0 ? (
        <div className="h-64 overflow-x-auto">
          <div className="flex items-end justify-between h-full space-x-2 min-w-full">
            {analytics.trends.callVolume.slice(-14).map((data, index) => {
              const maxCalls = Math.max(...analytics.trends.callVolume.map(d => d.calls));
              const height = maxCalls > 0 ? (data.calls / maxCalls) * 200 : 0;
              const successHeight = maxCalls > 0 ? (data.successfulCalls / maxCalls) * 200 : 0;
              return (
                <div key={index} className="flex flex-col items-center flex-1 min-w-0">
                  <div className="flex flex-col items-center justify-end h-48 w-full">
                    <div className="relative w-full">
                      <div 
                        className="bg-red-300 rounded-t w-full min-h-1"
                        style={{ height: `${height}px` }}
                        title={`${data.calls} total calls on ${new Date(data.date).toLocaleDateString()}`}
                      ></div>
                      <div 
                        className="bg-teal-500 rounded-t w-full absolute bottom-0"
                        style={{ height: `${successHeight}px` }}
                        title={`${data.successfulCalls} successful calls on ${new Date(data.date).toLocaleDateString()}`}
                      ></div>
                    </div>
                  </div>
                  <div className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} truncate w-full text-center`}>
                    {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} font-medium`}>
                    {data.calls}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center mt-4 space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-teal-500 rounded"></div>
              <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Successful</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-300 rounded"></div>
              <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center">
          <div className="text-center">
            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>📞</div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No call volume data available
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const RevenueTrendsChart = () => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        Revenue Trends
      </h3>
      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
        </div>
      ) : analytics?.trends?.revenue?.length > 0 ? (
        <div className="h-64 overflow-x-auto">
          <div className="flex items-end justify-between h-full space-x-2 min-w-full">
            {analytics.trends.revenue.slice(-14).map((data, index) => {
              const maxRevenue = Math.max(...analytics.trends.revenue.map(d => d.revenue));
              const height = maxRevenue > 0 ? (data.revenue / maxRevenue) * 200 : 0;
              return (
                <div key={index} className="flex flex-col items-center flex-1 min-w-0">
                  <div className="flex flex-col items-center justify-end h-48 w-full">
                    <div 
                      className="bg-green-500 rounded-t w-full min-h-1 transition-all duration-300"
                      style={{ height: `${height}px` }}
                      title={`$${data.revenue.toFixed(2)} revenue on ${new Date(data.date).toLocaleDateString()}`}
                    ></div>
                  </div>
                  <div className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} truncate w-full text-center`}>
                    {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} font-medium`}>
                    ${data.revenue.toFixed(0)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center">
          <div className="text-center">
            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>💰</div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No revenue data available
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const PlatformPerformanceMetrics = () => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border`}>
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Platform Performance Metrics
        </h3>
      </div>
      <div className="p-6">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className={`animate-pulse h-4 w-24 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
                <div className={`animate-pulse h-8 w-16 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
              </div>
            ))}
          </div>
        ) : analytics ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                Overall Success Rate
              </p>
              <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
                {analytics.platformMetrics.totalCalls > 0 
                  ? Math.round((analytics.platformMetrics.totalCalls / analytics.platformMetrics.totalCalls) * 100) 
                  : 0}%
              </div>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Platform-wide call success
              </p>
            </div>
            
            <div className="text-center">
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                Average Revenue per User
              </p>
              <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
                ${analytics.platformMetrics.activeUsers > 0 
                  ? (analytics.platformMetrics.totalRevenue / analytics.platformMetrics.activeUsers).toFixed(2)
                  : '0.00'}
              </div>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                ARPU for active users
              </p>
            </div>
            
            <div className="text-center">
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                Calls per Active User
              </p>
              <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
                {analytics.platformMetrics.activeUsers > 0 
                  ? Math.round(analytics.platformMetrics.totalCalls / analytics.platformMetrics.activeUsers)
                  : 0}
              </div>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Average usage per user
              </p>
            </div>
            
            <div className="text-center">
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                User Activation Rate
              </p>
              <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
                {analytics.platformMetrics.totalUsers > 0 
                  ? Math.round((analytics.platformMetrics.activeUsers / analytics.platformMetrics.totalUsers) * 100)
                  : 0}%
              </div>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Users with calls
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>⚡</div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No performance data available
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const handleExportAnalytics = async () => {
    if (!analytics) return;
    
    try {
      const exportData = {
        exportDate: new Date().toISOString(),
        timeRange: analytics.timeRange,
        platformMetrics: analytics.platformMetrics,
        topPerformingParrots: analytics.topPerformingParrots,
        trends: analytics.trends
      };

      // Create and download CSV
      const csvContent = generateCSV(exportData);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `parrotmate-analytics-${analytics.timeRange.type}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to export analytics:', error);
      alert('Failed to export analytics data');
    }
  };

  const generateCSV = (data) => {
    const lines = [];
    
    // Header
    lines.push('ParrotMate Analytics Export');
    lines.push(`Export Date: ${new Date(data.exportDate).toLocaleString()}`);
    lines.push(`Time Range: ${data.timeRange.type} (${data.timeRange.start} to ${data.timeRange.end})`);
    lines.push('');
    
    // Platform Metrics
    lines.push('Platform Metrics');
    lines.push('Metric,Value');
    lines.push(`Total Users,${data.platformMetrics.totalUsers}`);
    lines.push(`Active Users,${data.platformMetrics.activeUsers}`);
    lines.push(`Total Calls,${data.platformMetrics.totalCalls}`);
    lines.push(`Total Revenue,$${data.platformMetrics.totalRevenue}`);
    lines.push(`Active Parrots,${data.platformMetrics.activeParrots}`);
    lines.push(`Calls Today,${data.platformMetrics.callsToday}`);
    lines.push(`Revenue This Month,$${data.platformMetrics.revenueThisMonth}`);
    lines.push(`User Growth,${data.platformMetrics.userGrowth.percentChange}%`);
    lines.push(`Call Volume Growth,${data.platformMetrics.callVolumeGrowth.percentChange}%`);
    lines.push('');
    
    // Top Performing Parrots
    lines.push('Top Performing Parrots');
    lines.push('Parrot Name,User Name,Calls,Success Rate,Revenue');
    data.topPerformingParrots.forEach(parrot => {
      lines.push(`"${parrot.parrotName}","${parrot.userName}",${parrot.calls},${parrot.successRate}%,$${parrot.revenue}`);
    });
    lines.push('');
    
    // User Growth Trend
    if (data.trends.userGrowth.length > 0) {
      lines.push('User Growth Trend');
      lines.push('Date,New Users,Total Users');
      data.trends.userGrowth.forEach(trend => {
        lines.push(`${trend.date},${trend.newUsers},${trend.totalUsers}`);
      });
      lines.push('');
    }
    
    // Call Volume Trend
    if (data.trends.callVolume.length > 0) {
      lines.push('Call Volume Trend');
      lines.push('Date,Total Calls,Successful Calls');
      data.trends.callVolume.forEach(trend => {
        lines.push(`${trend.date},${trend.calls},${trend.successfulCalls}`);
      });
      lines.push('');
    }
    
    // Revenue Trend
    if (data.trends.revenue.length > 0) {
      lines.push('Revenue Trend');
      lines.push('Date,Revenue');
      data.trends.revenue.forEach(trend => {
        lines.push(`${trend.date},$${trend.revenue}`);
      });
    }
    
    return lines.join('\n');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Analytics Dashboard
          </h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Platform usage statistics and insights
          </p>
        </div>
        
        {/* Time Range Selector */}
        <div className="flex items-center space-x-2">
          <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Time Range:
          </label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className={`px-3 py-2 rounded-lg border transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white focus:ring-teal-500 focus:border-teal-500'
                : 'bg-white border-gray-300 text-gray-900 focus:ring-teal-500 focus:border-teal-500'
            }`}
          >
            <option value="1_day">Last 24 Hours</option>
            <option value="7_days">Last 7 Days</option>
            <option value="30_days">Last 30 Days</option>
            <option value="60_days">Last 60 Days</option>
            <option value="90_days">Last 90 Days</option>
            <option value="month_to_date">Month to Date</option>
          </select>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className={`${
          theme === 'dark' ? 'bg-red-900 border-red-700 text-red-200' : 'bg-red-50 border-red-200 text-red-700'
        } border rounded-lg p-4 mb-6`}>
          <div className="flex items-center">
            <span className="text-xl mr-2">⚠️</span>
            <div>
              <p className="font-medium">Failed to load analytics data</p>
              <p className="text-sm mt-1">{error}</p>
              <button
                onClick={fetchAnalytics}
                className="text-sm underline mt-2 hover:no-underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <MetricCard
          title="Total Users"
          value={analytics?.platformMetrics?.totalUsers?.toLocaleString() || '0'}
          icon="👥"
          trend={analytics?.platformMetrics?.userGrowth?.percentChange > 0 ? 'up' : analytics?.platformMetrics?.userGrowth?.percentChange < 0 ? 'down' : 'neutral'}
          trendValue={analytics?.platformMetrics?.userGrowth?.percentChange ? `${analytics.platformMetrics.userGrowth.percentChange > 0 ? '+' : ''}${analytics.platformMetrics.userGrowth.percentChange}% from last period` : null}
          isLoading={loading}
        />
        <MetricCard
          title="Total Calls"
          value={analytics?.platformMetrics?.totalCalls?.toLocaleString() || '0'}
          icon="📞"
          trend={analytics?.platformMetrics?.callVolumeGrowth?.percentChange > 0 ? 'up' : analytics?.platformMetrics?.callVolumeGrowth?.percentChange < 0 ? 'down' : 'neutral'}
          trendValue={analytics?.platformMetrics?.callVolumeGrowth?.percentChange ? `${analytics.platformMetrics.callVolumeGrowth.percentChange > 0 ? '+' : ''}${analytics.platformMetrics.callVolumeGrowth.percentChange}% from last period` : null}
          isLoading={loading}
        />
        <MetricCard
          title="Total Revenue"
          value={analytics?.platformMetrics?.totalRevenue ? `$${analytics.platformMetrics.totalRevenue.toLocaleString()}` : '$0'}
          icon="💰"
          isLoading={loading}
        />
        <MetricCard
          title="Active Parrots"
          value={analytics?.platformMetrics?.activeParrots?.toLocaleString() || '0'}
          icon="🦜"
          isLoading={loading}
        />
        <MetricCard
          title="Calls Today"
          value={analytics?.platformMetrics?.callsToday?.toLocaleString() || '0'}
          icon="📊"
          isLoading={loading}
        />
        <MetricCard
          title="Revenue This Month"
          value={analytics?.platformMetrics?.revenueThisMonth ? `$${analytics.platformMetrics.revenueThisMonth.toLocaleString()}` : '$0'}
          icon="📈"
          isLoading={loading}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CallVolumeChart />
        <RevenueTrendsChart />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UserGrowthChart />
        <TopPerformingParrots />
      </div>

      {/* Platform Performance Metrics */}
      <div className="space-y-6">
        <PlatformPerformanceMetrics />
        
        {/* Analytics Export Section */}
        <div className={`${
          theme === 'dark' 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        } rounded-lg shadow border`}>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Analytics Export
              </h3>
              <button
                onClick={handleExportAnalytics}
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:text-white"
              >
                {loading ? 'Exporting...' : 'Export Data'}
              </button>
            </div>
          </div>
          <div className="p-6">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              Export comprehensive analytics data including user metrics, call statistics, revenue trends, and platform performance data for the selected time period.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                  User Metrics
                </h4>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Total users, growth trends, active users
                </p>
              </div>
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                  Call Statistics
                </h4>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Call volume, success rates, parrot performance
                </p>
              </div>
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                  Revenue Data
                </h4>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Revenue trends, growth indicators, cost analysis
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// System Health Tab Component
const SystemHealthTab = () => {
  const { theme } = useTheme();
  const { getToken } = useAuth();
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchSystemHealth();
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchSystemHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSystemHealth = async () => {
    try {
      setError(null);
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/admin/system-health', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSystemHealth(data);
        setLastUpdated(new Date());
      } else {
        throw new Error('Failed to fetch system health data');
      }
    } catch (error) {
      console.error('Failed to fetch system health:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const HealthMetricCard = ({ title, status, value, unit, icon, details, threshold, alerts = [] }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'healthy': return 'text-green-600';
        case 'warning': return 'text-yellow-600';
        case 'critical': return 'text-red-600';
        default: return theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
      }
    };

    const getStatusBg = (status) => {
      switch (status) {
        case 'healthy': return 'bg-green-100 border-green-200';
        case 'warning': return 'bg-yellow-100 border-yellow-200';
        case 'critical': return 'bg-red-100 border-red-200';
        default: return theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
      }
    };

    const getStatusIcon = (status) => {
      switch (status) {
        case 'healthy': return '✅';
        case 'warning': return '⚠️';
        case 'critical': return '🚨';
        default: return '❓';
      }
    };

    return (
      <div className={`${
        theme === 'dark' && status === 'healthy' 
          ? 'bg-gray-800 border-gray-700' 
          : getStatusBg(status)
      } rounded-lg shadow border p-6 relative`}>
        {/* Warning indicator for critical/warning status */}
        {(status === 'critical' || status === 'warning') && (
          <div className="absolute top-2 right-2">
            <div className={`w-3 h-3 rounded-full ${
              status === 'critical' ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'
            }`}></div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">{icon}</span>
              <h3 className={`text-lg font-medium ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {title}
              </h3>
            </div>
            
            <div className="flex items-center space-x-2 mb-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                status === 'healthy' ? 'bg-green-600 text-white' :
                status === 'warning' ? 'bg-yellow-600 text-white' :
                status === 'critical' ? 'bg-red-600 text-white' :
                'bg-gray-600 text-white'
              }`}>
                {getStatusIcon(status)} {status?.toUpperCase() || 'UNKNOWN'}
              </span>
              {value !== undefined && (
                <span className={`text-2xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {value}{unit}
                </span>
              )}
            </div>

            {details && (
              <div className="space-y-1">
                {details.map((detail, index) => (
                  <div key={index} className={`text-sm ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    <span className="font-medium">{detail.label}:</span> {detail.value}
                  </div>
                ))}
              </div>
            )}

            {/* Component-specific alerts */}
            {alerts.length > 0 && (
              <div className="mt-3 space-y-1">
                {alerts.slice(0, 2).map((alert, index) => (
                  <div key={index} className={`text-xs p-2 rounded ${
                    alert.type === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200' :
                    alert.type === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200' :
                    'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200'
                  }`}>
                    {alert.message}
                  </div>
                ))}
                {alerts.length > 2 && (
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    +{alerts.length - 2} more alerts
                  </div>
                )}
              </div>
            )}

            {threshold && (
              <div className={`text-xs mt-2 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Threshold: {threshold}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const DatabaseHealthCard = () => {
    if (!systemHealth?.database) return null;
    
    const db = systemHealth.database;
    const storagePercent = db.storageLimit > 0 ? (db.storageUsed / db.storageLimit) * 100 : 0;
    const connectionPercent = db.maxConnections > 0 ? (db.connectionCount / db.maxConnections) * 100 : 0;
    
    // Generate component-specific alerts
    const dbAlerts = [];
    if (storagePercent > 80) {
      dbAlerts.push({
        type: storagePercent > 90 ? 'critical' : 'warning',
        message: `Storage usage at ${storagePercent.toFixed(1)}%`
      });
    }
    if (db.queryPerformance.errorRate > 1) {
      dbAlerts.push({
        type: db.queryPerformance.errorRate > 5 ? 'critical' : 'warning',
        message: `High error rate: ${db.queryPerformance.errorRate.toFixed(2)}%`
      });
    }
    if (db.queryPerformance.averageResponseTime > 100) {
      dbAlerts.push({
        type: 'warning',
        message: `Slow queries: ${db.queryPerformance.averageResponseTime.toFixed(1)}ms avg`
      });
    }
    
    return (
      <HealthMetricCard
        title="Database Health"
        status={db.status}
        icon="🗄️"
        details={[
          { label: 'Storage Used', value: `${db.storageUsed.toFixed(2)} GB / ${db.storageLimit} GB (${storagePercent.toFixed(1)}%)` },
          { label: 'Active Connections', value: `${db.connectionCount} / ${db.maxConnections} (${connectionPercent.toFixed(1)}%)` },
          { label: 'Avg Query Time', value: `${db.queryPerformance.averageResponseTime.toFixed(1)} ms` },
          { label: 'Slow Queries', value: db.queryPerformance.slowQueries },
          { label: 'Error Rate', value: `${db.queryPerformance.errorRate.toFixed(2)}%` }
        ]}
        threshold="Storage: 80%, Connections: 90%, Query Time: 100ms"
        alerts={dbAlerts}
      />
    );
  };

  const StorageHealthCard = () => {
    if (!systemHealth?.storage) return null;
    
    const storage = systemHealth.storage;
    
    // Generate storage-specific alerts
    const storageAlerts = [];
    if (storage.totalUsage > 500) {
      storageAlerts.push({
        type: storage.totalUsage > 1000 ? 'critical' : 'warning',
        message: `High storage usage: ${storage.totalUsage.toFixed(1)} GB`
      });
    }
    if (storage.totalCost > 50) {
      storageAlerts.push({
        type: storage.totalCost > 100 ? 'critical' : 'warning',
        message: `High monthly cost: $${storage.totalCost.toFixed(2)}`
      });
    }
    
    // Determine status based on usage and cost
    let status = 'healthy';
    if (storage.totalUsage > 1000 || storage.totalCost > 100) {
      status = 'critical';
    } else if (storage.totalUsage > 500 || storage.totalCost > 50) {
      status = 'warning';
    }
    
    return (
      <HealthMetricCard
        title="Storage Usage"
        status={status}
        value={storage.totalUsage.toFixed(1)}
        unit=" GB"
        icon="💾"
        details={[
          { label: 'Total Cost', value: `$${storage.totalCost.toFixed(2)}/month` },
          { label: 'Recordings Bucket', value: `${storage.r2Buckets.find(b => b.name.includes('recordings'))?.usage.toFixed(1) || 0} GB` },
          { label: 'Assets Bucket', value: `${storage.r2Buckets.find(b => b.name.includes('assets'))?.usage.toFixed(1) || 0} GB` },
          { label: 'Total Files', value: storage.r2Buckets.reduce((sum, bucket) => sum + bucket.fileCount, 0).toLocaleString() }
        ]}
        alerts={storageAlerts}
      />
    );
  };

  const WorkerPerformanceCard = () => {
    if (!systemHealth?.worker) return null;
    
    const worker = systemHealth.worker;
    
    // Generate worker-specific alerts
    const workerAlerts = [];
    if (worker.cpuUsage > 70) {
      workerAlerts.push({
        type: worker.cpuUsage > 85 ? 'critical' : 'warning',
        message: `High CPU usage: ${worker.cpuUsage.toFixed(1)}%`
      });
    }
    if (worker.memoryUsage > 75) {
      workerAlerts.push({
        type: worker.memoryUsage > 90 ? 'critical' : 'warning',
        message: `High memory usage: ${worker.memoryUsage.toFixed(1)}%`
      });
    }
    if (worker.errorRate > 1) {
      workerAlerts.push({
        type: worker.errorRate > 5 ? 'critical' : 'warning',
        message: `High error rate: ${worker.errorRate.toFixed(2)}%`
      });
    }
    if (worker.averageExecutionTime > 200) {
      workerAlerts.push({
        type: 'warning',
        message: `Slow execution: ${worker.averageExecutionTime.toFixed(1)}ms avg`
      });
    }
    
    return (
      <HealthMetricCard
        title="Worker Performance"
        status={worker.status}
        icon="⚡"
        details={[
          { label: 'Requests/min', value: worker.requestsPerMinute.toLocaleString() },
          { label: 'Avg Execution Time', value: `${worker.averageExecutionTime.toFixed(1)} ms` },
          { label: 'Error Rate', value: `${worker.errorRate.toFixed(2)}%` },
          { label: 'Memory Usage', value: `${worker.memoryUsage.toFixed(1)}%` },
          { label: 'CPU Usage', value: `${worker.cpuUsage.toFixed(1)}%` }
        ]}
        threshold="Execution: 200ms, Error Rate: 1%, Memory: 80%"
        alerts={workerAlerts}
      />
    );
  };

  const SystemHealthTrends = () => {
    const [trendsData, setTrendsData] = useState(null);
    const [trendsLoading, setTrendsLoading] = useState(false);
    const [trendsTimeRange, setTrendsTimeRange] = useState('24h');

    const fetchTrendsData = async (timeRange = '24h') => {
      try {
        setTrendsLoading(true);
        const token = await getToken();
        const response = await fetch(`https://parrotmate.com/api/admin/system-health/history?timeRange=${timeRange}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setTrendsData(data.history);
        }
      } catch (error) {
        console.error('Failed to fetch trends data:', error);
      } finally {
        setTrendsLoading(false);
      }
    };

    useEffect(() => {
      fetchTrendsData(trendsTimeRange);
    }, [trendsTimeRange]);

    const TrendChart = ({ title, data, dataKey, color = 'teal' }) => {
      if (!data || data.length === 0) {
        return (
          <div className="h-32 flex items-center justify-center">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No trend data available
            </p>
          </div>
        );
      }

      const maxValue = Math.max(...data.map(d => d[dataKey] || 0));
      
      return (
        <div className="space-y-2">
          <h4 className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {title}
          </h4>
          <div className="h-32 flex items-end justify-between space-x-1">
            {data.slice(-12).map((point, index) => {
              const height = maxValue > 0 ? ((point[dataKey] || 0) / maxValue) * 100 : 0;
              return (
                <div key={index} className="flex flex-col items-center flex-1">
                  <div className="flex flex-col items-center justify-end h-24 w-full">
                    <div 
                      className={`bg-${color}-500 rounded-t w-full min-h-1 transition-all duration-300`}
                      style={{ height: `${height}%` }}
                      title={`${point[dataKey] || 0} at ${point.timestamp || point.date}`}
                    ></div>
                  </div>
                  <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} truncate w-full text-center`}>
                    {point.timestamp ? 
                      new Date(point.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' }) :
                      new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div className={`${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      } rounded-lg shadow border`}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              System Health Trends
            </h3>
            <select
              value={trendsTimeRange}
              onChange={(e) => setTrendsTimeRange(e.target.value)}
              className={`px-3 py-1 rounded border text-sm transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-teal-500 focus:border-teal-500'
                  : 'bg-white border-gray-300 text-gray-900 focus:ring-teal-500 focus:border-teal-500'
              }`}
            >
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>
        </div>
        <div className="p-6">
          {trendsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500"></div>
            </div>
          ) : trendsData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <TrendChart 
                title="Database Storage Usage (%)" 
                data={trendsData.data} 
                dataKey="database_storage_usage" 
                color="blue" 
              />
              <TrendChart 
                title="Worker CPU Usage (%)" 
                data={trendsData.data} 
                dataKey="worker_cpu_usage" 
                color="green" 
              />
              <TrendChart 
                title="Total Alerts" 
                data={trendsData.data} 
                dataKey="total_alerts" 
                color="red" 
              />
              <TrendChart 
                title="Storage Usage (GB)" 
                data={trendsData.data} 
                dataKey="storage_total_usage" 
                color="purple" 
              />
              <TrendChart 
                title="Worker Memory Usage (%)" 
                data={trendsData.data} 
                dataKey="worker_memory_usage" 
                color="yellow" 
              />
              <TrendChart 
                title="Database Error Rate (%)" 
                data={trendsData.data} 
                dataKey="database_error_rate" 
                color="red" 
              />
            </div>
          ) : (
            <div className="text-center py-8">
              <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>📈</div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                No trend data available
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const SystemAlertsPanel = () => {
    if (!systemHealth?.alerts || systemHealth.alerts.length === 0) {
      return (
        <div className={`${
          theme === 'dark' 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        } rounded-lg shadow border p-6`}>
          <div className="flex items-center space-x-2 mb-4">
            <span className="text-2xl">🔔</span>
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              System Alerts
            </h3>
          </div>
          <div className="text-center py-8">
            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>✅</div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No active alerts - all systems operating normally
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={`${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      } rounded-lg shadow border`}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">🔔</span>
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              System Alerts ({systemHealth.alerts.length})
            </h3>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {systemHealth.alerts.map((alert) => (
              <div key={alert.id} className={`p-4 rounded-lg border ${
                alert.type === 'critical' ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' :
                alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800' :
                'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        alert.type === 'critical' ? 'bg-red-600 text-white' :
                        alert.type === 'warning' ? 'bg-yellow-600 text-white' :
                        'bg-blue-600 text-white'
                      }`}>
                        {alert.type.toUpperCase()}
                      </span>
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {new Date(alert.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {alert.message}
                    </p>
                  </div>
                  {alert.resolved === 0 && (
                    <button className="ml-4 px-3 py-1 bg-teal-600 text-white text-xs rounded hover:bg-teal-700 transition-colors dark:text-white">
                      Resolve
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const handleExportHealthData = async (format = 'json', timeRange = '7d') => {
    try {
      const token = await getToken();
      const response = await fetch(`https://parrotmate.com/api/admin/system-health/report?format=${format}&timeRange=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        let content, mimeType, filename;
        
        if (format === 'csv') {
          content = generateCSVReport(data);
          mimeType = 'text/csv;charset=utf-8;';
          filename = `parrotmate-system-health-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`;
        } else {
          content = JSON.stringify(data, null, 2);
          mimeType = 'application/json;charset=utf-8;';
          filename = `parrotmate-system-health-${timeRange}-${new Date().toISOString().split('T')[0]}.json`;
        }

        // Create and download file
        const blob = new Blob([content], { type: mimeType });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('Failed to generate report');
      }
    } catch (error) {
      console.error('Failed to export system health data:', error);
      alert('Failed to export system health data');
    }
  };

  const generateCSVReport = (data) => {
    const lines = [];
    
    // Header
    lines.push('ParrotMate System Health Report');
    lines.push(`Generated: ${new Date(data.generated_at).toLocaleString()}`);
    lines.push(`Time Range: ${data.time_range}`);
    lines.push(`Current Status: ${data.current_status}`);
    lines.push('');
    
    // Summary
    lines.push('Summary');
    lines.push('Metric,Value');
    lines.push(`Total Data Points,${data.summary.total_data_points}`);
    lines.push(`Average Uptime,${data.summary.avg_uptime_percentage}%`);
    lines.push(`Total Alerts,${data.summary.total_alerts}`);
    lines.push(`Critical Incidents,${data.summary.critical_incidents}`);
    lines.push('');
    
    // Historical Data
    if (data.historical_data && data.historical_data.length > 0) {
      lines.push('Historical Data');
      lines.push('Timestamp,Overall Status,Database Status,Storage Status,Worker Status,Total Alerts,Critical Alerts,DB Storage %,DB Error %,Storage GB,Worker CPU %,Worker Memory %');
      
      data.historical_data.forEach(point => {
        lines.push([
          point.timestamp || point.date,
          point.overall_status,
          point.database_status,
          point.storage_status,
          point.worker_status,
          point.total_alerts || 0,
          point.critical_alerts || 0,
          point.metrics?.database_storage_usage || 0,
          point.metrics?.database_error_rate || 0,
          point.metrics?.storage_total_usage || 0,
          point.metrics?.worker_cpu_usage || 0,
          point.metrics?.worker_memory_usage || 0
        ].join(','));
      });
    }
    
    return lines.join('\n');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            System Health Monitoring
          </h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Real-time infrastructure monitoring and performance metrics
          </p>
          {lastUpdated && (
            <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} mt-1`}>
              Last updated: {lastUpdated.toLocaleString()}
            </p>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchSystemHealth}
            disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:text-white"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={handleExportHealthData}
            disabled={loading || !systemHealth}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:text-white"
          >
            Export Data
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className={`${
          theme === 'dark' ? 'bg-red-900 border-red-700 text-red-200' : 'bg-red-50 border-red-200 text-red-700'
        } border rounded-lg p-4 mb-6`}>
          <div className="flex items-center">
            <span className="text-xl mr-2">⚠️</span>
            <div>
              <p className="font-medium">Failed to load system health data</p>
              <p className="text-sm mt-1">{error}</p>
              <button
                onClick={fetchSystemHealth}
                className="text-sm underline mt-2 hover:no-underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !systemHealth && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-4"></div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Loading system health data...</p>
          </div>
        </div>
      )}

      {/* Health Metrics Grid */}
      {systemHealth && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <DatabaseHealthCard />
            <StorageHealthCard />
            <WorkerPerformanceCard />
          </div>

          {/* System Alerts */}
          <SystemAlertsPanel />

          {/* System Health Trends */}
          <SystemHealthTrends />

          {/* System Health Export Section */}
          <div className={`${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          } rounded-lg shadow border`}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  System Health Export & Reporting
                </h3>
              </div>
            </div>
            <div className="p-6">
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
                Export comprehensive system health data including database metrics, storage usage, worker performance, historical trends, and alert history for monitoring and analysis.
              </p>
              
              {/* Export Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Export Format
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="exportFormat"
                        value="json"
                        defaultChecked
                        className="mr-2 text-teal-600 focus:ring-teal-500"
                      />
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        JSON (Detailed data structure)
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="exportFormat"
                        value="csv"
                        className="mr-2 text-teal-600 focus:ring-teal-500"
                      />
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        CSV (Spreadsheet compatible)
                      </span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Time Range
                  </label>
                  <select
                    name="exportTimeRange"
                    defaultValue="7d"
                    className={`w-full px-3 py-2 rounded-lg border transition-colors ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-teal-500 focus:border-teal-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-teal-500 focus:border-teal-500'
                    }`}
                  >
                    <option value="24h">Last 24 Hours</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                  </select>
                </div>
              </div>

              {/* Export Buttons */}
              <div className="flex space-x-3 mb-6">
                <button
                  onClick={() => {
                    const format = document.querySelector('input[name="exportFormat"]:checked').value;
                    const timeRange = document.querySelector('select[name="exportTimeRange"]').value;
                    handleExportHealthData(format, timeRange);
                  }}
                  disabled={loading}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:text-white"
                >
                  {loading ? 'Exporting...' : 'Export Report'}
                </button>
                <button
                  onClick={() => handleExportHealthData('json', 'current')}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:text-white"
                >
                  Export Current Status
                </button>
              </div>

              {/* Export Content Types */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                    📊 Current Metrics
                  </h4>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Real-time database, storage, and worker performance metrics
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                    📈 Historical Trends
                  </h4>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Performance trends, uptime statistics, and usage patterns
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                    🚨 Alert History
                  </h4>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Alert timeline, incident reports, and resolution tracking
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Modal Components (simplified for now)

const AssignNumberModal = ({ availableNumbers, onClose, onRefresh }) => {
  const { theme } = useTheme();
  return (
    <div className={`fixed inset-0 ${theme === 'dark' ? 'bg-gray-900' : 'bg-black'} bg-opacity-75 flex items-center justify-center z-50`}>
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto border`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Available Numbers</h3>
          <button
            onClick={onClose}
            className={`${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
          >
            ✕
          </button>
        </div>
        <div className="space-y-2">
          {availableNumbers.map((number, index) => (
            <div key={index} className={`flex justify-between items-center p-3 border rounded-lg transition-colors ${
              theme === 'dark' ? 'border-gray-600 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
            }`}>
              <div>
                <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{number.phoneNumber}</span>
                {number.locality && (
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {number.locality}, {number.region}
                  </div>
                )}
              </div>
              <button className="px-3 py-1 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 transition-colors dark:text-white">
                Assign
              </button>
            </div>
          ))}
          {availableNumbers.length === 0 && (
            <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              No available numbers found. Try searching with different criteria.
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className={`px-4 py-2 text-white rounded transition-colors ${
              theme === 'dark' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-500 hover:bg-gray-600'
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ParrotModal = ({ parrot, onClose, onRefresh }) => {
  const { theme } = useTheme();
  return (
    <div className={`fixed inset-0 ${theme === 'dark' ? 'bg-gray-900' : 'bg-black'} bg-opacity-75 flex items-center justify-center z-50`}>
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto border`}>
        <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Edit Parrot</h3>
        <div className="space-y-4">
          <div>
            <strong className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Name:</strong> <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{parrot.name}</span>
          </div>
          <div>
            <strong className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Owner:</strong> <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{parrot.user_email}</span>
          </div>
          <div>
            <strong className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>First Message:</strong>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mt-1`}>{parrot.first_message}</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className={`px-4 py-2 text-white rounded transition-colors ${
              theme === 'dark' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-500 hover:bg-gray-600'
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const PromoCodeModal = ({ promo, onClose, onRefresh }) => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    code: promo?.code || '',
    description: promo?.description || '',
    type: promo?.type || 'percentage',
    discount_value: promo?.discount_value || '',
    max_uses: promo?.max_uses || '',
    valid_from: promo?.valid_from ? promo.valid_from.split('T')[0] : '',
    valid_until: promo?.valid_until ? promo.valid_until.split('T')[0] : '',
    is_active: promo?.is_active ?? true
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = await getToken();
      const url = promo
        ? `https://parrotmate.com/api/admin/promo/${promo.id}`
        : 'https://parrotmate.com/api/admin/promo';
      
      const method = promo ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          discount_value: parseFloat(formData.discount_value),
          max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
          valid_from: formData.valid_from || null,
          valid_until: formData.valid_until || null
        })
      });

      if (response.ok) {
        onRefresh();
        onClose();
      } else {
        const error = await response.json();
        alert(`Failed to save promo code: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to save promo code:', error);
      alert('Failed to save promo code');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`fixed inset-0 ${theme === 'dark' ? 'bg-gray-900' : 'bg-black'} bg-opacity-75 flex items-center justify-center z-50`}>
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg p-6 max-w-md w-full mx-4 border`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {promo ? 'Edit Promo Code' : 'Create New Promo Code'}
          </h3>
          <button
            onClick={onClose}
            className={`${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
          >
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
              Promo Code
            </label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
              className={`w-full px-3 py-2 border rounded-lg transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="SAVE20"
              required
            />
          </div>

          <div>
            <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className={`w-full px-3 py-2 border rounded-lg transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="20% off first month"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Discount Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className={`w-full px-3 py-2 border rounded-lg transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="percentage">Percentage</option>
                <option value="fixed_amount">Fixed Amount</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Discount Value
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.discount_value}
                onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                className={`w-full px-3 py-2 border rounded-lg transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder={formData.type === 'percentage' ? '20' : '5.00'}
                required
              />
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
              Max Uses (leave empty for unlimited)
            </label>
            <input
              type="number"
              min="1"
              value={formData.max_uses}
              onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
              className={`w-full px-3 py-2 border rounded-lg transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="100"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Valid From (optional)
              </label>
              <input
                type="date"
                value={formData.valid_from}
                onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                className={`w-full px-3 py-2 border rounded-lg transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Valid Until (optional)
              </label>
              <input
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                className={`w-full px-3 py-2 border rounded-lg transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className={`h-4 w-4 text-teal-600 focus:ring-teal-500 rounded ${
                theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'
              }`}
            />
            <label htmlFor="is_active" className={`ml-2 block text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Active
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 text-white rounded transition-colors ${
                theme === 'dark' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-500 hover:bg-gray-600'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 transition-colors dark:text-white"
            >
              {saving ? 'Saving...' : (promo ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
